import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { collection, getDocs, getFirestore, orderBy, query, where } from '@firebase/firestore';
import { app } from '../../firebase.js';
import LeftBar from '../../Components/LeftBar';
import Dashnav from '../../Components/Dashnav';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import Table from 'react-bootstrap/Table';
import Badge from 'react-bootstrap/Badge';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowsRotate,
  faMagnifyingGlass,
  faUserCheck,
  faUserClock,
  faUserXmark
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import defaultProfilePicture from '../../Images/defprofile.png';
import { toDateValue } from '../../utils/financeWorkflow';

const ATTENDANCE_COLLECTION = 'AttendaceReport';
const SCHOOL_SESSION_TITLE = 'School Check-In';
const NAIROBI_TIME_ZONE = 'Africa/Nairobi';
const JOIN_TYPE_OPTIONS = ['All', 'Physical', 'Virtual', 'Both', 'Absent'];
const STATUS_OPTIONS = ['All', 'Present', 'Signed in', 'Absent'];

const normalizeText = (value) => String(value || '').trim().toLowerCase();

const getRecordTimeValue = (record) => {
  return record?.checkInTime?.getTime?.() || record?.checkOutTime?.getTime?.() || 0;
};

const uniqueRecords = (records = []) => {
  return Array.from(new Map(records.map((record) => [record.id, record])).values());
};

const pickLatestRecord = (records = []) => {
  return [...records].sort((left, right) => getRecordTimeValue(right) - getRecordTimeValue(left))[0] || null;
};

const formatDateInputValue = (value = new Date()) => {
  const dateValue = toDateValue(value) || new Date();
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: NAIROBI_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(dateValue);
};

const getDateKey = (value) => {
  const dateValue = toDateValue(value);
  if (!dateValue) {
    return '';
  }

  return new Intl.DateTimeFormat('en-CA', {
    timeZone: NAIROBI_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(dateValue);
};

const formatDateTime = (value, fallback = '—') => {
  const dateValue = toDateValue(value);
  if (!dateValue) {
    return fallback;
  }

  return dateValue.toLocaleString('en-KE', {
    timeZone: NAIROBI_TIME_ZONE,
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
};

const formatDuration = (startTime, endTime) => {
  const startValue = toDateValue(startTime);
  const endValue = toDateValue(endTime);

  if (!startValue && !endValue) {
    return '—';
  }

  if (startValue && !endValue) {
    return 'Open';
  }

  if (!startValue || !endValue) {
    return '—';
  }

  const diffMinutes = Math.max(0, Math.round((endValue.getTime() - startValue.getTime()) / 60000));
  const hours = Math.floor(diffMinutes / 60);
  const minutes = diffMinutes % 60;

  if (hours === 0) {
    return `${minutes}m`;
  }

  return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
};

const formatLocationStatus = (locationCheck = {}) => {
  const distance = Number(locationCheck.distanceInMeters);
  const radius = Number(locationCheck.radiusInMeters || 200);

  if (!Number.isFinite(distance)) {
    return 'Location not captured';
  }

  if (distance <= radius) {
    return `Verified within ${Math.round(distance)}m`;
  }

  return `${Math.round(distance)}m away`;
};

const resolveAttendanceType = (record = {}) => {
  const sessionTitle = normalizeText(record.sessionTitle || record.className);
  const mode = normalizeText(record.mode || record.attendanceMode);
  const hasRoomLink = Boolean(String(record.roomlink || '').trim());
  const hasVirtualSignals = hasRoomLink
    || mode === 'online'
    || sessionTitle.includes('live')
    || sessionTitle.includes('virtual')
    || Boolean(record.startTime || record.endTime);
  const hasPhysicalSignals = sessionTitle === normalizeText(SCHOOL_SESSION_TITLE)
    || sessionTitle.includes('school check')
    || mode === 'onsite'
    || Boolean(record.locationCheck);

  if (hasVirtualSignals && !hasPhysicalSignals) {
    return 'Virtual';
  }

  if (hasPhysicalSignals) {
    return 'Physical';
  }

  return null;
};

const resolveStatusMeta = (record) => {
  if (!record) {
    return {
      label: 'Absent',
      tone: 'secondary',
      icon: faUserXmark,
      description: 'No school sign-in recorded',
      progress: 'No attendance entry'
    };
  }

  if (record.isActive) {
    return {
      label: 'Signed in',
      tone: 'warning',
      icon: faUserClock,
      description: 'Currently checked in',
      progress: 'Open session'
    };
  }

  return {
    label: 'Present',
    tone: 'success',
    icon: faUserCheck,
    description: 'Checked in and checked out',
    progress: 'Session completed'
  };
};

const resolveJoinLabel = (row) => {
  if (row.hasPhysical && row.hasVirtual) {
    return 'Physical + Virtual';
  }

  if (row.hasPhysical) {
    return 'Physical';
  }

  if (row.hasVirtual) {
    return 'Virtual';
  }

  return 'Absent';
};

function DailyAttendanceRegister() {
  const db = getFirestore(app);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedDate, setSelectedDate] = useState(formatDateInputValue());
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [joinTypeFilter, setJoinTypeFilter] = useState('All');
  const [students, setStudents] = useState([]);
  const [attendanceRecords, setAttendanceRecords] = useState([]);

  const fetchAttendanceRegister = useCallback(async () => {
      setRefreshing(true);

      try {
        const [usersSnapshot, attendanceSnapshot] = await Promise.all([
          getDocs(query(
            collection(db, 'users'),
            where('userStatus', '==', 'Student'),
            orderBy('appDate', 'desc')
          )),
          getDocs(collection(db, ATTENDANCE_COLLECTION))
        ]);

      const studentRows = [];
      usersSnapshot.forEach((docItem) => {
        const data = docItem.data();
        const userType = normalizeText(data.userType);
        const userStatus = normalizeText(data.userStatus);
        if (userType !== 'student' && userStatus !== 'student') {
          return;
        }

        studentRows.push({
          id: docItem.id,
          userId: data.userId || docItem.id,
          bio: data.bio || data.displayName || data.name || 'Unnamed student',
          course: data.course || 'Course not set',
          email: data.userEmail || data.email || '',
          phoneNumber: data.phoneNumber || data.phone || '',
          profilePic: data.profilePic || defaultProfilePicture,
          branchName: data.branchName || data.campus || data.selectedCampus || '',
          admissionNumber: data.admissionNumber || data.prospectAdmissionNumber || '',
          status: data.userStatus || data.userType || 'Student'
        });
      });

      const normalizedRecords = attendanceSnapshot.docs
        .map((docItem) => {
          const data = docItem.data();
          const checkInTime = toDateValue(data.signInTime || data.startTime || data.createdAt);
          const checkOutTime = toDateValue(data.signOutTime || data.endTime);
          const sessionTitle = data.sessionTitle || data.className || 'Class Session';
          const mode = data.mode || data.attendanceMode || (data.roomlink ? 'Online' : 'Onsite');
          const dateKey = getDateKey(checkInTime || checkOutTime || data.createdAt);
          const studentId = data.userId || data.studentId || '';
          const locationCheck = data.locationCheck || {};
          const attendanceType = resolveAttendanceType(data);
          const locationVerified = Number.isFinite(Number(locationCheck.distanceInMeters))
            && Number(locationCheck.distanceInMeters) <= Number(locationCheck.radiusInMeters || 200);

          return {
            id: docItem.id,
            studentId,
            studentName: data.username || data.studentName || 'Student',
            sessionTitle,
            mode,
            attendanceType,
            status: data.status || (checkOutTime ? 'Present' : 'Ongoing'),
            checkInTime,
            checkOutTime,
            isActive: Boolean(checkInTime && !checkOutTime),
            dateKey,
            locationCheck,
            locationVerified,
            notes: data.notes || data.reason || '',
            raw: data,
            isSchoolCheckIn:
              normalizeText(sessionTitle) === normalizeText(SCHOOL_SESSION_TITLE)
              || (normalizeText(sessionTitle).includes('school check') && normalizeText(mode) === 'onsite')
          };
        })
        .filter((record) => record.attendanceType)
        .sort((first, second) => (second.checkInTime?.getTime?.() || 0) - (first.checkInTime?.getTime?.() || 0));

      setStudents(studentRows);
      setAttendanceRecords(normalizedRecords);
    } catch (error) {
      console.error('Failed to load attendance register:', error);
      setStudents([]);
      setAttendanceRecords([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [db]);

  useEffect(() => {
    fetchAttendanceRegister();
  }, [fetchAttendanceRegister]);

  const selectedDateLabel = useMemo(() => {
    const parsed = toDateValue(selectedDate);
    if (!parsed) {
      return 'Selected day';
    }

    return parsed.toLocaleDateString('en-KE', {
      timeZone: NAIROBI_TIME_ZONE,
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  }, [selectedDate]);

  const registerRows = useMemo(() => {
    const rowsByStudent = new Map();

    attendanceRecords.forEach((record) => {
      if (record.dateKey !== selectedDate || !record.studentId) {
        return;
      }

      if (!rowsByStudent.has(record.studentId)) {
        rowsByStudent.set(record.studentId, []);
      }

      rowsByStudent.get(record.studentId).push(record);
    });

    return students
      .map((student) => {
        const studentRecords = uniqueRecords([
          ...(rowsByStudent.get(student.userId) || []),
          ...(rowsByStudent.get(student.id) || [])
        ]);
        const physicalAttendance = pickLatestRecord(studentRecords.filter((record) => record.attendanceType === 'Physical'));
        const virtualAttendance = pickLatestRecord(studentRecords.filter((record) => record.attendanceType === 'Virtual'));
        const overallAttendance = pickLatestRecord(studentRecords);

        return {
          ...student,
          physicalAttendance,
          virtualAttendance,
          overallAttendance,
          hasPhysical: Boolean(physicalAttendance),
          hasVirtual: Boolean(virtualAttendance),
          joinLabel: resolveJoinLabel({
            hasPhysical: Boolean(physicalAttendance),
            hasVirtual: Boolean(virtualAttendance)
          }),
          sortName: student.bio.toLowerCase()
        };
      })
      .sort((first, second) => first.sortName.localeCompare(second.sortName));
  }, [attendanceRecords, selectedDate, students]);

  const visibleRows = useMemo(() => {
    const queryText = searchTerm.trim().toLowerCase();

    return registerRows.filter((row) => {
      const attendance = row.overallAttendance;
      const statusMeta = resolveStatusMeta(attendance);

      if (statusFilter !== 'All' && statusMeta.label !== statusFilter) {
        return false;
      }

      if (joinTypeFilter === 'Physical' && !row.hasPhysical) {
        return false;
      }

      if (joinTypeFilter === 'Virtual' && !row.hasVirtual) {
        return false;
      }

      if (joinTypeFilter === 'Both' && !(row.hasPhysical && row.hasVirtual)) {
        return false;
      }

      if (joinTypeFilter === 'Absent' && (row.hasPhysical || row.hasVirtual)) {
        return false;
      }

      if (!queryText) {
        return true;
      }

      const haystack = [
        row.bio,
        row.course,
        row.email,
        row.phoneNumber,
        row.admissionNumber,
        row.joinLabel,
        row.physicalAttendance?.sessionTitle,
        row.physicalAttendance?.mode,
        row.virtualAttendance?.sessionTitle,
        row.virtualAttendance?.mode,
        attendance?.sessionTitle,
        attendance?.mode,
        attendance?.status
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return haystack.includes(queryText);
    });
  }, [joinTypeFilter, registerRows, searchTerm, statusFilter]);

  const stats = useMemo(() => {
    const totalStudents = registerRows.length;
    const signedIn = registerRows.filter((row) => Boolean(row.overallAttendance)).length;
    const physicalJoined = registerRows.filter((row) => row.hasPhysical).length;
    const virtualJoined = registerRows.filter((row) => row.hasVirtual).length;
    const bothJoined = registerRows.filter((row) => row.hasPhysical && row.hasVirtual).length;
    const active = registerRows.filter((row) => row.overallAttendance?.isActive).length;
    const absent = totalStudents - signedIn;
    const verified = registerRows.filter((row) => row.physicalAttendance?.locationVerified).length;
    const completedDurations = attendanceRecords
      .filter((record) => !record.isActive && record.checkInTime && record.checkOutTime)
      .map((record) => record.checkOutTime.getTime() - record.checkInTime.getTime());
    const present = registerRows.filter((row) => row.overallAttendance && !row.overallAttendance.isActive).length;

    const averageMinutes = completedDurations.length === 0
      ? 0
      : Math.round(completedDurations.reduce((sum, value) => sum + value, 0) / completedDurations.length / 60000);

    return {
      totalStudents,
      signedIn,
      physicalJoined,
      virtualJoined,
      bothJoined,
      present,
      active,
      absent,
      verified,
      attendanceRate: totalStudents > 0 ? Math.round((signedIn / totalStudents) * 100) : 0,
      averageMinutes
    };
  }, [attendanceRecords, registerRows]);

  const latestSignIn = useMemo(() => {
    const signedRows = registerRows
      .filter((row) => row.overallAttendance)
      .sort((first, second) => (second.overallAttendance?.checkInTime?.getTime?.() || 0) - (first.overallAttendance?.checkInTime?.getTime?.() || 0));

    return signedRows[0] || null;
  }, [registerRows]);

  const formatAverageDuration = useCallback((minutes) => {
    if (!minutes) {
      return '0m';
    }

    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    if (hours === 0) {
      return `${remainingMinutes}m`;
    }

    return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
  }, []);

  const statusBadgeClass = useCallback((label) => {
    if (label === 'Present') {
      return 'attendanceStatusPill attendanceStatusPill--present';
    }

    if (label === 'Signed in') {
      return 'attendanceStatusPill attendanceStatusPill--active';
    }

    return 'attendanceStatusPill attendanceStatusPill--absent';
  }, []);

  const joinFilterButtons = useMemo(() => JOIN_TYPE_OPTIONS.map((option) => ({
    label: option === 'Physical'
      ? 'Physical joined'
      : option === 'Virtual'
        ? 'Virtual joined'
        : option === 'Both'
          ? 'Both'
          : option === 'Absent'
            ? 'Absent'
            : 'All'
  })), []);

  const handleViewStudent = useCallback((studentId) => {
    if (!studentId) {
      return;
    }

    navigate('/view-student', { state: { myData: studentId } });
  }, [navigate]);

  return (
    <div className="mainContainer">
      <LeftBar />
      <div className="theMainContainer">
        <Dashnav />
        <div className="clientsComp applicationsPage attendanceRegisterPage dailyAttendancePage">
          <section className="appsHeader">
            <div className="attendanceHeaderCopy">
              <p className="appsKicker">Data Analytics</p>
                <h4>Daily Attendance Register</h4>
                <p className="appsSubtext">
                Physical school check-ins and virtual class sign-ins both land in <strong>AttendaceReport</strong>.
                This register opens with your active students only, grouped by student and date so you can review attendance in one place.
              </p>
              <div className="attendanceHeaderTags">
                <span className="attendanceHeaderTag">Physical</span>
                <span className="attendanceHeaderTag">Virtual</span>
                <span className="attendanceHeaderTag">Shared collection</span>
              </div>
            </div>
            <div className="appsHeaderStats">
              <div className="appsStatChip">
                <span>Active students</span>
                <strong>{stats.totalStudents}</strong>
              </div>
              <div className="appsStatChip">
                <span>Physical</span>
                <strong>{stats.physicalJoined}</strong>
              </div>
              <div className="appsStatChip">
                <span>Virtual</span>
                <strong>{stats.virtualJoined}</strong>
              </div>
              <div className="appsStatChip">
                <span>Both</span>
                <strong>{stats.bothJoined}</strong>
              </div>
              <div className="appsStatChip">
                <span>Absent</span>
                <strong>{stats.absent}</strong>
              </div>
            </div>
          </section>

          <section className="appsFiltersCard attendanceFiltersCard">
            <div className="appsSearch input-group attendanceSearch">
              <span className="input-group-text">
                <FontAwesomeIcon icon={faMagnifyingGlass} />
              </span>
              <Form.Control
                type="search"
                placeholder="Search student, course, phone, or admission number..."
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
              />
            </div>

            <div className="attendanceToolbarRow">
              <Form.Group className="attendanceDateControl">
                <Form.Label>Register day</Form.Label>
                <Form.Control
                  type="date"
                  value={selectedDate}
                  onChange={(event) => setSelectedDate(event.target.value)}
                />
              </Form.Group>
              <Form.Group className="attendanceTypeControl">
                <Form.Label>Join type</Form.Label>
                <div className="attendanceTypeToggle" role="tablist" aria-label="Join type filter">
                  {joinFilterButtons.map((option, index) => {
                    const value = JOIN_TYPE_OPTIONS[index];
                    const isActive = joinTypeFilter === value;

                    return (
                      <button
                        key={value}
                        type="button"
                        className={`attendanceTypeButton ${isActive ? 'attendanceTypeButton--active' : ''}`}
                        onClick={() => setJoinTypeFilter(value)}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              </Form.Group>
              <Form.Select
                className="filterSelect attendanceStatusSelect"
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value)}
              >
                {STATUS_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option === 'All' ? 'All statuses' : option}
                  </option>
                ))}
              </Form.Select>
            </div>

            <div className="incomeToolbarMeta attendanceMeta">
              <span className="incomeToolbarText">{selectedDateLabel}</span>
              <span className="incomeInlineDot" />
              <span className="incomeToolbarText">{visibleRows.length} visible students</span>
              <span className="incomeInlineDot" />
              <span className="incomeToolbarText">Source: shared attendance collection</span>
            </div>

            <Button variant="outline-dark" onClick={fetchAttendanceRegister} disabled={refreshing}>
              <FontAwesomeIcon icon={faArrowsRotate} className="me-2" />
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
          </section>

          <section className="attendanceMiniSummary">
            <div className="attendanceMiniSummaryItem">
              <span>Attendance rate</span>
              <strong>{stats.attendanceRate}%</strong>
            </div>
            <div className="attendanceMiniSummaryItem">
              <span>Active sessions</span>
              <strong>{stats.active}</strong>
            </div>
            <div className="attendanceMiniSummaryItem">
              <span>Completed sessions</span>
              <strong>{stats.present}</strong>
            </div>
            <div className="attendanceMiniSummaryItem">
              <span>Average duration</span>
              <strong>{formatAverageDuration(stats.averageMinutes)}</strong>
            </div>
            <div className="attendanceMiniSummaryItem">
              <span>GPS verified</span>
              <strong>{stats.verified}</strong>
            </div>
          </section>
          <div className="attendanceLatestNote">
            {latestSignIn
              ? `Latest activity: ${latestSignIn.bio || latestSignIn.studentName || 'Student'} at ${formatDateTime(latestSignIn.overallAttendance?.checkInTime)}`
              : 'No attendance entries were recorded for the selected day yet.'}
          </div>

          {loading ? (
            <div className="incomeStateCard">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading attendance register...</span>
              </Spinner>
              <p>Loading the daily attendance register...</p>
            </div>
          ) : (
            <section className="appsTableCard attendanceTableCard">
              <div className="appsTableHeader">
                <h6>
                  <FontAwesomeIcon icon={faUserCheck} className="me-2" />
                  Register for {selectedDateLabel}
                </h6>
                <span>{visibleRows.length} students</span>
              </div>

              <div className="appsTableWrap attendanceTableWrap">
                <Table hover responsive className="appsTable attendanceTable">
                  <thead>
                    <tr>
                      <th>Student</th>
                      <th>Course</th>
                      <th>Physical</th>
                      <th>Virtual</th>
                      <th>Status</th>
                      <th>Latest activity</th>
                      <th>Session span</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleRows.map((student) => {
                      const attendance = student.overallAttendance;
                      const physicalAttendance = student.physicalAttendance;
                      const virtualAttendance = student.virtualAttendance;
                      const statusMeta = resolveStatusMeta(attendance);

                      return (
                        <tr key={student.userId || student.id}>
                          <td>
                            <div className="appsStudentCell attendanceStudentCell">
                              <div className="appDefProfPic attendanceStudentAvatar">
                                <img src={student.profilePic || defaultProfilePicture} alt={student.bio} />
                              </div>
                              <div>
                                <p className="appsStudentName">{student.bio}</p>
                                <span className="appsStudentMeta">
                                  {student.email || student.userId}
                                  {student.admissionNumber ? ` • ${student.admissionNumber}` : ''}
                                </span>
                              </div>
                            </div>
                          </td>
                          <td>{student.course}</td>
                          <td>
                            {physicalAttendance ? (
                              <div className="attendanceRecordCell">
                                <span className="attendanceTypeBadge attendanceTypeBadge--physical">Physical</span>
                                <strong>{formatDateTime(physicalAttendance.checkInTime)}</strong>
                                <span>{physicalAttendance.isActive ? 'Open school session' : 'School session completed'}</span>
                              </div>
                            ) : (
                              <div className="attendanceRecordCell attendanceRecordCell--empty">
                                <strong>Not joined</strong>
                                <span>No physical check-in</span>
                              </div>
                            )}
                          </td>
                          <td>
                            {virtualAttendance ? (
                              <div className="attendanceRecordCell">
                                <span className="attendanceTypeBadge attendanceTypeBadge--virtual">Virtual</span>
                                <strong>{formatDateTime(virtualAttendance.checkInTime)}</strong>
                                <span>{virtualAttendance.sessionTitle}</span>
                              </div>
                            ) : (
                              <div className="attendanceRecordCell attendanceRecordCell--empty">
                                <strong>Not joined</strong>
                                <span>No virtual class sign-in</span>
                              </div>
                            )}
                          </td>
                          <td>
                            <Badge bg="light" text="dark" className={statusBadgeClass(statusMeta.label)}>
                              <FontAwesomeIcon icon={statusMeta.icon} className="me-2" />
                              {statusMeta.label}
                            </Badge>
                            <div className="attendanceRowNote">{statusMeta.description}</div>
                          </td>
                          <td>
                            {attendance ? (
                              <div className="attendanceRecordCell">
                                <strong>{attendance.sessionTitle || 'Class Session'}</strong>
                                <span>
                                  {resolveJoinLabel(student)}
                                  {' '}
                                  •
                                  {' '}
                                  {attendance.mode || '—'}
                                </span>
                                <span>
                                  {attendance.isSchoolCheckIn
                                    ? formatLocationStatus(attendance.locationCheck)
                                    : attendance.roomlink
                                      ? 'Room link recorded'
                                      : 'Session recorded'}
                                </span>
                              </div>
                            ) : (
                              <div className="attendanceRecordCell attendanceRecordCell--empty">
                                <strong>No activity</strong>
                                <span>Absent for the selected day</span>
                              </div>
                            )}
                          </td>
                          <td>
                            {attendance ? (
                              <div className="attendanceRecordCell">
                                <strong>{formatDuration(attendance.checkInTime, attendance.checkOutTime)}</strong>
                                <span>
                                  {attendance.checkInTime ? formatDateTime(attendance.checkInTime) : '—'}
                                  {' '}
                                  -
                                  {' '}
                                  {attendance.checkOutTime ? formatDateTime(attendance.checkOutTime) : 'Open'}
                                </span>
                              </div>
                            ) : (
                              <div className="attendanceRecordCell attendanceRecordCell--empty">
                                <strong>—</strong>
                                <span>No session span</span>
                              </div>
                            )}
                          </td>
                          <td>
                            <button
                              type="button"
                              className="appsViewBtn"
                              onClick={() => handleViewStudent(student.userId || student.id)}
                            >
                              View
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                    {visibleRows.length === 0 && (
                      <tr>
                        <td colSpan="8" className="appsEmptyState">
                          No attendance rows match the selected day and filters.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

export default DailyAttendanceRegister;
